import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge } from "@/components/ui/badge";
import { Container, MapPin, Truck, Layers, Wrench, X } from "lucide-react";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { TrailerDetailDialog } from "@/components/yard-view/TrailerDetailDialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTrailerImageUrls } from "@/lib/trailer-image";
import { useUrlFilterState, useUrlArrayFilterState } from "@/hooks/useUrlFilterState";
import { TrailerFilterBar } from "@/components/trailers/TrailerFilterBar";
import {
  DOCS_FILTER_OPTIONS,
  DWELL_FILTER_OPTIONS,
  EVENT_FILTER_OPTIONS,
  SERVICE_FILTER_OPTIONS,
  YARD_STATUS_FILTER_OPTIONS,
  toArrayOrNull,
  type TrailerFilterKey,
  type TrailerFilterOption,
  type TrailerListRow,
} from "@/lib/trailer-filters";
import {
  LoadStatusCell,
  TrailerAssociatedOrdersCell,
  TrailerDocumentsCell,
  TrailerDwellCell,
  TrailerLocationCell,
  TrailerRecentEventCell,
  TrailerStatusCell,
  type LocationLookupRow,
} from "@/components/trailers/TrailerRowCells";
import { TrailerCard } from "@/components/trailers/TrailerCard";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";

const YARD_FILTER_KEYS: readonly TrailerFilterKey[] = [
  "type", "scac", "status", "dwell", "event", "docs", "service", "location",
];

const COLUMN_COUNT = 11;

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function YardView() {
  const [selectedTrailerId, setSelectedTrailerId] = useState<string | null>(null);
  const { data: trailerTypesData } = useTrailerTypes();
  const { tenantId } = useTenant();

  const [searchQuery, setSearchQuery] = useUrlFilterState<string>("q", "");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [groupBy, setGroupBy] = useUrlFilterState<"location" | "none">("group", "location");

  // Filter state (URL-persisted, same as /trailers)
  const [typeFilter, setTypeFilter] = useUrlArrayFilterState("type");
  const [scacFilter, setScacFilter] = useUrlArrayFilterState("scac");
  const [statusFilter, setStatusFilter] = useUrlArrayFilterState("status");
  const [dwellFilter, setDwellFilter] = useUrlArrayFilterState("dwell");
  const [eventFilter, setEventFilter] = useUrlArrayFilterState("event");
  const [docsFilter, setDocsFilter] = useUrlArrayFilterState("docs");
  const [serviceFilter, setServiceFilter] = useUrlArrayFilterState("service");
  const [locationFilter, setLocationFilter] = useUrlArrayFilterState("location");

  const filterValues: Partial<Record<TrailerFilterKey, string[]>> = {
    type: typeFilter,
    scac: scacFilter,
    status: statusFilter,
    dwell: dwellFilter,
    event: eventFilter,
    docs: docsFilter,
    service: serviceFilter,
    location: locationFilter,
  };

  const setFilter = (key: TrailerFilterKey, value: string[]) => {
    const setters: Record<TrailerFilterKey, (v: string[]) => void> = {
      type: setTypeFilter,
      scac: setScacFilter,
      status: setStatusFilter,
      dwell: setDwellFilter,
      event: setEventFilter,
      docs: setDocsFilter,
      service: setServiceFilter,
      location: setLocationFilter,
    };
    setters[key]?.(value);
  };

  const resetAllFilters = () => {
    setTypeFilter(["all"]);
    setScacFilter(["all"]);
    setStatusFilter(["all"]);
    setDwellFilter(["all"]);
    setEventFilter(["all"]);
    setDocsFilter(["all"]);
    setServiceFilter(["all"]);
    setLocationFilter(["all"]);
    setSearchQuery("");
  };

  // Server-side: filtering, latest event, dwell, doc counts and associated orders
  // are all computed by the database so nothing is truncated by row caps.
  const { data: yardData, isLoading } = useQuery({
    queryKey: [
      "yard-view-trailers", tenantId, debouncedSearch.trim(),
      typeFilter, scacFilter, statusFilter, dwellFilter, eventFilter, docsFilter, serviceFilter, locationFilter,
    ],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_yard_trailers" as any, {
        p_tenant_id: tenantId!,
        p_search: debouncedSearch.trim() || null,
        p_type_ids: toArrayOrNull(typeFilter),
        p_scacs: toArrayOrNull(scacFilter),
        p_statuses: toArrayOrNull(statusFilter),
        p_dwell: toArrayOrNull(dwellFilter),
        p_event: toArrayOrNull(eventFilter),
        p_docs: toArrayOrNull(docsFilter),
        p_location_ids: toArrayOrNull(locationFilter),
        p_service: toArrayOrNull(serviceFilter),
      });
      if (error) throw error;
      const row = (data as any[])?.[0];
      return {
        rows: (row?.rows ?? []) as TrailerListRow[],
        totalCount: Number(row?.total_count ?? 0),
        totalAtSite: Number(row?.total_at_site ?? 0),
        totalInTransit: Number(row?.total_in_transit ?? 0),
      };
    },
    placeholderData: (prev) => prev,
  });

  const filteredTrailers = yardData?.rows ?? [];
  const totalAtSite = yardData?.totalAtSite ?? 0;
  const totalInTransit = yardData?.totalInTransit ?? 0;

  const { data: allLocations } = useQuery({
    queryKey: ["locations-short-codes"],
    queryFn: async () => {
      const { data, error } = await supabase.from("locations").select("id, short_code, city").eq("archived", false);
      if (error) throw error;
      return data as LocationLookupRow[];
    },
  });

  // Tenant-wide SCAC list so the dropdown isn't limited to the visible rows.
  const { data: scacRows } = useQuery({
    queryKey: ["trailer-scacs", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_trailer_scacs_for_tenant" as any, {
        p_tenant_id: tenantId!,
      });
      if (error) throw error;
      return (data as { scac: string }[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const filterOptions: Partial<Record<TrailerFilterKey, TrailerFilterOption[]>> = useMemo(() => ({
    type: (trailerTypesData ?? []).map((t) => ({ label: t.label, value: t.id })),
    scac: (scacRows ?? []).map((r) => ({ label: r.scac, value: r.scac })),
    status: YARD_STATUS_FILTER_OPTIONS,
    dwell: DWELL_FILTER_OPTIONS,
    event: EVENT_FILTER_OPTIONS,
    docs: DOCS_FILTER_OPTIONS,
    service: SERVICE_FILTER_OPTIONS,
    location: [...(allLocations ?? [])]
      .filter((l) => l.short_code)
      .sort((a, b) => a.short_code.localeCompare(b.short_code))
      .map((l) => ({ label: l.city ? `${l.short_code}: ${l.city}` : l.short_code, value: l.id })),
  }), [trailerTypesData, scacRows, allLocations]);

  // Pagination runs on the full ordered dataset first; grouping is then derived
  // from the visible page so both view modes show the same rows.
  //
  // When grouping by location the rows are ordered by their rendered group
  // first (sites alphabetically, then unassigned, then in-transit) and only
  // then by trailer ID. That keeps each group contiguous in the dataset, so a
  // page slice fills up across group boundaries instead of dropping trailers
  // that sort late in the alphabet out of their yard group.
  const orderedTrailers = useMemo(() => {
    const byTrailerId = (a: TrailerListRow, b: TrailerListRow) =>
      a.trailer_id.localeCompare(b.trailer_id, undefined, { numeric: true, sensitivity: "base" });

    if (groupBy !== "location") return [...filteredTrailers].sort(byTrailerId);

    // 0 = a site group, 1 = unassigned / unknown, 2 = in transit.
    const bucket = (t: TrailerListRow) => {
      if (t.current_location_status !== "at_site") return 2;
      return t.current_location?.short_code ? 0 : 1;
    };

    return [...filteredTrailers].sort((a, b) => {
      const bucketDiff = bucket(a) - bucket(b);
      if (bucketDiff !== 0) return bucketDiff;
      const siteDiff = (a.current_location?.short_code ?? "").localeCompare(
        b.current_location?.short_code ?? "",
      );
      if (siteDiff !== 0) return siteDiff;
      return byTrailerId(a, b);
    });
  }, [filteredTrailers, groupBy]);


  const {
    page, setPage, pageSize, setPageSize, totalPages, totalCount: pagedTotalCount, pageRows,
  } = useTablePagination<TrailerListRow>(orderedTrailers);

  const atSiteTrailers = pageRows.filter((t) => t.current_location_status === "at_site");
  const inTransitTrailers = pageRows.filter((t) => t.current_location_status === "in_transit");

  // Group at-site trailers by site short code
  const grouped = new Map<string, TrailerListRow[]>();
  const unassigned: TrailerListRow[] = [];
  for (const trailer of atSiteTrailers) {
    const siteName = trailer.current_location?.short_code;
    if (!siteName) { unassigned.push(trailer); continue; }
    if (!grouped.has(siteName)) grouped.set(siteName, []);
    grouped.get(siteName)!.push(trailer);
  }
  const sortedSites = [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));

  // Group totals come from the full ordered dataset, not just the current
  // page, so a site header never under-reports when its trailers spill onto a
  // later page (see "2 of 5 shown").
  const groupTotals = useMemo(() => {
    const sites = new Map<string, number>();
    let unassignedTotal = 0;
    let inTransitTotal = 0;
    for (const t of orderedTrailers) {
      if (t.current_location_status !== "at_site") { inTransitTotal += 1; continue; }
      const code = t.current_location?.short_code;
      if (!code) { unassignedTotal += 1; continue; }
      sites.set(code, (sites.get(code) ?? 0) + 1);
    }
    return { sites, unassignedTotal, inTransitTotal };
  }, [orderedTrailers]);

  const siteTotal = (siteName: string) => groupTotals.sites.get(siteName) ?? 0;

  const flatTrailers = pageRows;

  // The `trailer-images` bucket is private, so URLs are signed client-side.
  const trailerImageUrls = useTrailerImageUrls(filteredTrailers.map((t) => t.image_path));
  const getImageUrl = (imagePath: string | null) => (imagePath ? trailerImageUrls[imagePath] ?? null : null);

  const hasActiveFilter =
    !!searchQuery.trim() ||
    YARD_FILTER_KEYS.some((k) => toArrayOrNull(filterValues[k]) !== null);

  const rowProps = { getImageUrl, onSelect: setSelectedTrailerId, trailerTypesData, allLocations };

  return (
    <AppLayout title="Yard View">
      <div className="space-y-6 animate-fade-in">
        <div className="grid grid-cols-3 gap-3">
          <SummaryTile icon={<Container className="h-5 w-5 text-primary" />} tone="bg-primary/10" value={totalAtSite + totalInTransit} label="Total Trailers" />
          <SummaryTile icon={<MapPin className="h-5 w-5 text-green-600" />} tone="bg-green-500/10" value={totalAtSite} label="At Sites" />
          <SummaryTile icon={<Truck className="h-5 w-5 text-blue-600" />} tone="bg-blue-500/10" value={totalInTransit} label="In Transit" />
        </div>

        <TrailerFilterBar
          keys={YARD_FILTER_KEYS}
          values={filterValues}
          options={filterOptions}
          onChange={setFilter}
          onResetAll={resetAllFilters}
          leadingSlot={
            <>
              <SearchChip query={searchQuery} onClear={() => setSearchQuery("")} />
              {groupBy === "location" ? (
                <Button
                  variant="outline"
                  className="gap-1.5 h-9 text-sm border-primary bg-primary/10 text-primary dark:text-[hsl(var(--aurora-tx-link))] hover:bg-primary/15 hover:text-primary dark:hover:text-[hsl(var(--aurora-tx-link))]"
                  onClick={() => setGroupBy("none")}
                >
                  <Layers className="h-3.5 w-3.5" />
                  Grouped by Location
                  <X className="h-3.5 w-3.5 ml-0.5 opacity-70 hover:opacity-100" />
                </Button>
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-1.5 h-9 text-sm">
                      <Layers className="h-3.5 w-3.5" />
                      Group by
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-1" align="start" sideOffset={6}>
                    <p className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Group by</p>
                    <button
                      className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent transition-colors text-left"
                      onClick={() => setGroupBy("location")}
                    >
                      <span className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        Location
                      </span>
                    </button>
                  </PopoverContent>
                </Popover>
              )}
            </>
          }
          trailingSlot={
            <>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Showing {filteredTrailers.length} trailer{filteredTrailers.length === 1 ? "" : "s"}
              </span>
              <ExpandingSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search trailers, sites or orders..." />
            </>
          }
        />

        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Loading trailers…</div>
        ) : filteredTrailers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {hasActiveFilter ? "No trailers match your filter criteria" : "No trailers found"}
          </div>
        ) : (
          <div className="rounded-lg border bg-card">
            <ResponsiveTableShell
              cards={
                <div className="flex flex-col gap-3 p-3">
                  {groupBy === "location"
                    ? [
                        ...sortedSites.map(([siteName, siteTrailers]) => ({
                          siteName,
                          trailers: siteTrailers,
                          totalCount: siteTotal(siteName),
                          muted: false,
                        })),
                        ...(unassigned.length > 0
                          ? [{ siteName: "Unassigned / Unknown", trailers: unassigned, totalCount: groupTotals.unassignedTotal, muted: true }]
                          : []),
                        ...(inTransitTrailers.length > 0
                          ? [{ siteName: "In Transit", trailers: inTransitTrailers, totalCount: groupTotals.inTransitTotal, muted: true }]
                          : []),
                      ].map((group) => (
                        <div key={group.siteName} className="flex flex-col gap-3">
                          <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2">
                            <span
                              className={`text-sm font-semibold ${group.muted ? "text-muted-foreground" : "text-foreground"}`}
                            >
                              {group.siteName}
                            </span>
                            <Badge variant="outline" className="text-xs">{group.totalCount}</Badge>
                            {group.totalCount > group.trailers.length && (
                              <span className="text-xs text-muted-foreground">
                                {group.trailers.length} of {group.totalCount} shown
                              </span>
                            )}
                          </div>
                          {group.trailers.map((trailer) => (
                            <TrailerCard
                              key={trailer.id}
                              trailer={trailer}
                              typeLabel={getTrailerTypeLabel(trailer.trailer_type_id, trailerTypesData) ?? "Unknown"}
                              allLocations={allLocations as any}
                              imageUrl={getImageUrl(trailer.image_path)}
                              variant="yard"
                              onClick={(t) => setSelectedTrailerId(t.id)}
                            />
                          ))}
                        </div>
                      ))
                    : flatTrailers.map((trailer) => (
                        <TrailerCard
                          key={trailer.id}
                          trailer={trailer}
                          typeLabel={getTrailerTypeLabel(trailer.trailer_type_id, trailerTypesData) ?? "Unknown"}
                          allLocations={allLocations as any}
                          imageUrl={getImageUrl(trailer.image_path)}
                          variant="yard"
                          onClick={(t) => setSelectedTrailerId(t.id)}
                        />
                      ))}
                </div>
              }
              table={
            <Table noStickyColumns>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px] sticky left-0 z-20 bg-background">
                    <span className="sr-only">Image</span>
                  </TableHead>
                  <TableHead className="sticky left-[60px] z-20 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">Trailer ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Load Status</TableHead>
                  <TableHead>SCAC</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dwell Time</TableHead>
                  <TableHead>Most Recent Event</TableHead>
                  <TableHead>Associated Orders</TableHead>
                  <TableHead>Documents</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupBy === "location" ? (
                  <>
                    {sortedSites.map(([siteName, siteTrailers]) => (
                      <SiteGroup key={siteName} siteName={siteName} trailers={siteTrailers} totalCount={siteTotal(siteName)} {...rowProps} />
                    ))}
                    {unassigned.length > 0 && (
                      <SiteGroup siteName="Unassigned / Unknown" trailers={unassigned} totalCount={groupTotals.unassignedTotal} muted {...rowProps} />
                    )}
                    {inTransitTrailers.length > 0 && (
                      <SiteGroup siteName="In Transit" trailers={inTransitTrailers} totalCount={groupTotals.inTransitTotal} muted {...rowProps} />
                    )}
                  </>
                ) : (
                  flatTrailers.map((trailer) => (
                    <TrailerRow key={trailer.id} trailer={trailer} {...rowProps} />
                  ))
                )}
              </TableBody>
            </Table>
              }
            />
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={pagedTotalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </div>

      <TrailerDetailDialog
        trailerId={selectedTrailerId}
        open={!!selectedTrailerId}
        onOpenChange={(open) => !open && setSelectedTrailerId(null)}
      />
    </AppLayout>
  );
}

function SummaryTile({ icon, tone, value, label }: { icon: ReactNode; tone: string; value: number; label: string }) {
  return (
    <div className="rounded-lg border bg-card p-4 flex items-center gap-3">
      <div className={`rounded-md p-2 ${tone}`}>{icon}</div>
      <div>
        <p className="text-2xl font-semibold leading-none">{value}</p>
        <p className="text-sm text-muted-foreground mt-1">{label}</p>
      </div>
    </div>
  );
}

interface RowProps {
  getImageUrl: (path: string | null) => string | null;
  onSelect: (id: string) => void;
  trailerTypesData: any;
  allLocations: LocationLookupRow[] | undefined;
}

function SiteGroup({
  siteName,
  trailers,
  totalCount,
  muted = false,
  ...rowProps
}: RowProps & { siteName: string; trailers: TrailerListRow[]; totalCount?: number; muted?: boolean }) {
  // `totalCount` is the group's size across every page; `trailers` is only the
  // slice on the current page.
  const total = totalCount ?? trailers.length;
  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        <TableCell colSpan={COLUMN_COUNT} className="py-2 p-0">
          {/* Sticky inner content stays anchored to the left edge while the
              row background spans the full table width. */}
          <div className="sticky left-0 inline-flex items-center gap-2 px-4 py-2">
            <span className={`text-sm font-semibold ${muted ? "text-muted-foreground" : "text-foreground"}`}>
              {siteName}
            </span>
            <Badge variant="outline" className="text-xs">{total}</Badge>
            {total > trailers.length && (
              <span className="text-xs text-muted-foreground">
                {trailers.length} of {total} shown
              </span>
            )}
          </div>
        </TableCell>
      </TableRow>
      {trailers.map((trailer) => (
        <TrailerRow key={trailer.id} trailer={trailer} {...rowProps} />
      ))}
    </>
  );
}

function TrailerRow({
  trailer,
  getImageUrl,
  onSelect,
  trailerTypesData,
  allLocations,
}: RowProps & { trailer: TrailerListRow }) {
  const imageUrl = getImageUrl(trailer.image_path);
  return (
    <TableRow
      className="cursor-pointer"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, input, a, [role="checkbox"]')) return;
        onSelect(trailer.id);
      }}
    >
      <TableCell className="w-[60px] sticky left-0 z-10 bg-inherit">
        {imageUrl ? (
          <img src={imageUrl} alt={trailer.trailer_id} className="h-10 w-10 rounded object-cover" />
        ) : (
          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
            <Container className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
      </TableCell>
      <TableCell className="sticky left-[60px] z-10 bg-inherit shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
        <div className="flex items-center gap-2">
          <span className="font-medium">{trailer.trailer_id}</span>
          {trailer.needs_service && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="destructive" className="gap-1 px-1.5 py-0 h-5 text-[10px] font-medium">
                  <Wrench className="h-3 w-3" />
                  Service
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-medium mb-0.5">Needs Service</p>
                <p className="text-xs whitespace-pre-wrap">
                  {trailer.needs_service_reason?.trim() || "No reason provided"}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell>{getTrailerTypeLabel(trailer.trailer_type_id, trailerTypesData) ?? "Unknown"}</TableCell>
      <TableCell><LoadStatusCell loadStatus={trailer.load_status} /></TableCell>
      <TableCell>{trailer.scac ?? "—"}</TableCell>
      <TableCell className="whitespace-nowrap">
        <TrailerLocationCell trailer={trailer} allLocations={allLocations} showCity />
      </TableCell>
      <TableCell><TrailerStatusCell status={trailer.current_location_status} sinceIso={trailer.latest_event?.occurred_at} /></TableCell>
      <TableCell className="whitespace-nowrap"><TrailerDwellCell trailer={trailer} /></TableCell>
      <TableCell className="whitespace-nowrap"><TrailerRecentEventCell event={trailer.latest_event} /></TableCell>
      <TableCell><TrailerAssociatedOrdersCell orders={trailer.associated_orders} /></TableCell>
      <TableCell><TrailerDocumentsCell trailer={trailer} /></TableCell>
    </TableRow>
  );
}
